import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import {
  PATH_ORGANIZATION_ADMIN,
  PATH_ORGANIZATION_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ThirdPartiesCmp_SomeOrganizations_QueryDocument } from '../../components/ThirdParties/queries/some-organizations.query';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { OrganizationPage_OneOrganization_QueryDocument } from './hooks/useFindOneOrganizationQuery.hook';

export const OrganizationPage_Admin_Organization_Fragment = graphql(`
  fragment OrganizationPage_Admin_Organization_Fragment on Organization {
    createdAt
    creatorId
    _id
    isInternal
    statusId
    updatedAt
    updaterId
  }
`);

export const OrganizationPage_Admin_Organization_Foapu_Mutation = graphql(`
  mutation OrganizationPage_Admin_Organization_Foapu(
    $filter: IdFilterInput!
    $update: OrganizationPartialInput!
    $versionData: VersionDataInput
  ) {
    findOneAndPartialUpdateOrganization(
      filter: $filter
      update: $update
      versionData: $versionData
    ) {
      _id
    }
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_Admin_Organization_Fragment
  >;
}

export const Admin = ({ organization: pOrganization }: Readonly<Props>) => {
  const { genericStatusesCodeMap } = useReferencesContext();

  const organization = getFragmentData(
    OrganizationPage_Admin_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_ADMIN,
      edit: PATH_ORGANIZATION_ADMIN_EDIT,
    },
    { id: organization._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuOrganizationMutation } = useGraphQLMutation(
    OrganizationPage_Admin_Organization_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(OrganizationPage_OneOrganization_QueryDocument),
            { id: organization._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
            {
              statusId: genericStatusesCodeMap.Disabled._id,
              isInternal: organization.isInternal,
            },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
            {
              statusId: genericStatusesCodeMap.Enabled._id,
              isInternal: organization.isInternal,
            },
          ],
        });
      };

      foapuOrganizationMutation(
        {
          filter: {
            _id: organization._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      enqueueSnackbar,
      foapuOrganizationMutation,
      genericStatusesCodeMap.Disabled._id,
      genericStatusesCodeMap.Enabled._id,
      leaveEditionMode,
      memos,
      organization._id,
      organization.isInternal,
      queryClient,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={organization.createdAt}
        creatorId={organization.creatorId}
        id={organization._id}
        statusId={organization.statusId}
        updatedAt={organization.updatedAt}
        updaterId={organization.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'organizationAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={organization.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
